import { useBillingNavigation } from "v2/pages/billing/hooks/useBillingNavigation";
import Card from "components/Card";
import Button from "components/Button";
import CheckedComponent from "components/CheckedComponent";
import Badge from "components/Badge";
import { Tooltip } from "components/Tooltip";
import PlanBadge from "./PlanBadge";
import LimitedOfferRibbon from "./LimitedOfferRibbon";
import { fmtNumber } from "utils";
import { formatPlanDisplayPrice, formatRegionLabel, getPlanTier } from "utils/planUtils";
import { PLAN_TIER_FEATURES } from "constants/planTierFeatures";
import Plan from "types/Plan";


interface PlanCardV2Props {
  plan: Plan;
  isCurrentPlan: boolean;
  recommendedPlanSlug?: string;
  isDowngrade?: boolean;
  scheduledPlanSlug?: string;
  originalMonthlyPriceLabel?: string;
  isYearly?: boolean;
  autoUpgradeEnabled?: boolean;
  limitedOfferText?: string;
  bonusCredits?: number;
}

export default function PlanCardV2({
  plan,
  isCurrentPlan,
  recommendedPlanSlug,
  isDowngrade = false,
  scheduledPlanSlug,
  originalMonthlyPriceLabel,
  isYearly,
  autoUpgradeEnabled,
  limitedOfferText,
  bonusCredits = 0,
}: PlanCardV2Props) {
  const { navigate } = useBillingNavigation();

  const showRecommendedBadge = recommendedPlanSlug === plan.planSlug;
  const isScheduledPlan = scheduledPlanSlug === plan.planSlug;
  const isAutoUpgradeTarget = autoUpgradeEnabled && showRecommendedBadge;

  const handlePlanClick = () => {
    if (isCurrentPlan || isScheduledPlan) return;
    if (isAutoUpgradeTarget) {
      navigate("auto-upgrade/disable", { viewTransition: true });
      return;
    }
    navigate(isDowngrade ? `downgrade/${plan.planSlug}` : `upgrade/${plan.planSlug}`);
  };

  const displayPlanName = plan.planName;
  const tierFeatures = PLAN_TIER_FEATURES[getPlanTier(plan.planSlug)] || [];
  const formattedPrice = plan.price ? formatPlanDisplayPrice(plan.price, plan.currencyCode, plan.planSlug) : "$0";
  const showBilledAnnually = isYearly && plan.planSlug !== "free";

  const ctaButton = isCurrentPlan ? (
    <Button text="YOUR CURRENT PLAN" className="button button-tertiary rounded w-full !bg-neutral-50 !text-[#465c6f] pointer-events-none" size="SM" disabled />
  ) : isScheduledPlan ? (
    <Button text="CHANGE SCHEDULED" className="button button-tertiary rounded w-full !bg-neutral-50 !text-[#465c6f] pointer-events-none" size="SM" disabled />
  ) : isAutoUpgradeTarget ? (
    <Button text="DISABLE AUTO-UPGRADE" className="button button-primary rounded w-full" size="SM" onClick={handlePlanClick} />
  ) : isDowngrade ? (
    <Button text="DOWNGRADE" className="button button-secondary rounded w-full" size="SM" onClick={handlePlanClick} />
  ) : showRecommendedBadge ? (
    <Button text="UPGRADE NOW" className="button button-primary rounded w-full" size="SM" onClick={handlePlanClick} />
  ) : (
    <Button text="UPGRADE NOW" className="button button-secondary rounded w-full" size="SM" onClick={handlePlanClick} />
  );

  return (
    <Card
      borderless
      fullHeight
      className={`relative min-w-0 !h-full rounded-lg border !overflow-visible ${
        showRecommendedBadge
          ? "!border-2 !border-brandPrimary-600 dark:!border-brandPrimary-400 !bg-brandLightest-50 dark:!bg-rebrandBlue-900/20"
          : "!border-neutral-200 dark:!border-neutral-700"
      }`}
    >
      {showRecommendedBadge && <PlanBadge variant="recommended" />}

      <div className="flex flex-col w-full h-full">
        <div className="px-4 pt-4">
          <h3 className="text-2xl font-semibold leading-8 text-neutral-900 dark:text-white whitespace-nowrap">
            {displayPlanName}
          </h3>

          <div className="flex flex-col gap-1 mt-4">
            {originalMonthlyPriceLabel ? (
              <p className="text-sm leading-5 text-[#465c6f] line-through whitespace-nowrap">
                {originalMonthlyPriceLabel}
              </p>
            ) : null}
            <div className="flex items-end gap-1 whitespace-nowrap">
              <span className="text-4xl font-semibold leading-10 text-neutral-900 dark:text-white">
                {formattedPrice}
              </span>
              <span className="text-sm font-normal leading-5 text-[#465c6f]">
                / month
              </span>
            </div>
            {showBilledAnnually && (
              <p className="text-sm leading-5 text-[#465c6f] mt-1">Billed annually</p>
            )}
          </div>

          <div className="mt-4">{ctaButton}</div>
        </div>

        <div className="mt-4">
          <hr className="border-t border-neutral-200 dark:border-neutral-700" />
          {limitedOfferText && <LimitedOfferRibbon text={limitedOfferText} />}
        </div>

        <div className="mt-4 px-4 pb-4 flex flex-col gap-3">
          <CheckedComponent variant="circle" highlighted={showRecommendedBadge} iconSize="w-4 h-4" className="text-sm text-gray-900 dark:text-white">
            {fmtNumber(plan.apiCreditLimit - bonusCredits)} API Credits
          </CheckedComponent>
          <CheckedComponent variant="circle" highlighted={showRecommendedBadge} iconSize="w-4 h-4" className="text-sm text-gray-900 dark:text-white">
            {plan.apiConcurrencyLimit} Concurrent Threads
          </CheckedComponent>
          {plan.planSlug !== "free" && (
            <CheckedComponent variant="circle" highlighted={showRecommendedBadge} iconSize="w-4 h-4" className="text-sm text-gray-900 dark:text-white">
              {formatRegionLabel(plan.apiGeotargeting)}
            </CheckedComponent>
          )}
          {tierFeatures.map((feature) => (
            <CheckedComponent
              key={feature.label}
              variant="circle"
              highlighted={showRecommendedBadge}
              iconSize="w-4 h-4"
              className="text-sm text-gray-900 dark:text-white"
            >
              <span className="inline-flex items-center gap-2">
                {feature.infoTooltip ? (
                  <Tooltip content={feature.infoTooltip}>{feature.label}</Tooltip>
                ) : (
                  feature.label
                )}
                {feature.badge === "new" && <Badge textColorClass="text-gray-900" />}
              </span>
            </CheckedComponent>
          ))}
        </div>
      </div>
    </Card>
  );
}
